<script setup>
import { ref, watch, computed } from 'vue';
import draggable from 'vuedraggable';
import KanbanCard from './KanbanCard.vue';

const props = defineProps({
  column: { type: Object, required: true },
  cards: { type: Array, default: () => [] },
  selectionMode: { type: Boolean, default: false },
  selectedIds: { type: Array, default: () => [] },
});

const emit = defineEmits([
  'cardMoved',
  'openConversation',
  'cardClick',
  'openTasks',
  'cardSelected',
  'openDescription',
  'openAutomations',
  'openCreateCard',
]);

const localCards = ref([...props.cards]);
watch(
  () => props.cards,
  val => {
    localCards.value = [...val];
  }
);

const totalValue = computed(() => {
  const sum = props.cards.reduce((acc, card) => {
    const v = card.value;
    return v !== null && v !== undefined && v !== '' ? acc + Number(v) : acc;
  }, 0);
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(sum);
});

const avgDays = computed(() => {
  const withDate = props.cards.filter(c => c.stage_changed_at);
  if (!withDate.length) return null;
  const now = Date.now();
  const total = withDate.reduce((acc, c) => {
    const ts =
      c.stage_changed_at > 1e10
        ? c.stage_changed_at
        : c.stage_changed_at * 1000;
    return acc + (now - ts) / 86400000;
  }, 0);
  const avg = total / withDate.length;
  if (avg < 1) return `${(avg * 24).toFixed(0)}h`;
  if (Number.isInteger(Math.round(avg * 10) / 10)) return `${Math.round(avg)}d`;
  return `${avg.toFixed(1)}d`;
});

// o card de cima na lista visível é a âncora: o índice sozinho não serve porque a
// lista pode estar filtrada e o backend posiciona na etapa inteira
function cardAbove(newIndex) {
  return newIndex > 0 ? (localCards.value[newIndex - 1]?.id ?? null) : null;
}

function onCardMoved(event) {
  const change = event.added ?? event.moved;
  if (!change) return;
  emit('cardMoved', {
    cardId: change.element.id,
    columnId: props.column.id,
    position: change.newIndex,
    afterCardId: cardAbove(change.newIndex),
  });
}
</script>

<template>
  <div
    class="flex flex-col w-[85vw] md:w-[285px] flex-shrink-0 h-full min-h-0 overflow-hidden rounded-lg bg-[#FEFEFE] dark:bg-[#141517] border border-n-weak"
  >
    <div class="group px-3 py-2 border-b border-n-weak">
      <div class="flex items-center justify-between">
        <h3 class="font-semibold text-sm text-n-slate-12 truncate">
          {{ column.name }}
        </h3>
        <span
          class="text-xs text-n-slate-10 bg-n-alpha-1 rounded-full px-2 py-0.5 flex-shrink-0 ml-2"
        >
          {{ cards.length }}
        </span>
      </div>
      <div class="flex items-center justify-between mt-0.5">
        <div class="flex items-center gap-2">
          <span class="text-xs text-n-slate-10">
            {{ avgDays ?? '—' }}
          </span>
          <div
            class="flex items-center gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
          >
            <button
              v-tooltip="
                column.description
                  ? {
                      content: column.description,
                      delay: { show: 300, hide: 0 },
                    }
                  : $t('KANBAN.COLUMN.DESCRIPTION_TOOLTIP')
              "
              class="flex items-center justify-center text-n-slate-10 hover:text-n-slate-12 transition-colors p-1.5 md:p-0.5"
              @click.stop="
                emit('openDescription', {
                  column,
                  anchor: $event.currentTarget.getBoundingClientRect(),
                })
              "
            >
              <span class="i-lucide-align-left size-3.5" />
            </button>
            <button
              :title="$t('KANBAN.COLUMN.AUTOMATIONS_TOOLTIP')"
              class="flex items-center justify-center text-n-slate-10 hover:text-n-slate-12 transition-colors p-1.5 md:p-0.5"
              @click.stop="emit('openAutomations', column)"
            >
              <span class="i-lucide-bot size-3.5" />
            </button>
            <button
              :title="$t('KANBAN.COLUMN.ADD_CARD_TOOLTIP')"
              class="flex items-center justify-center text-n-slate-10 hover:text-n-slate-12 transition-colors p-1.5 md:p-0.5"
              @click.stop="emit('openCreateCard', column)"
            >
              <span class="i-lucide-plus size-3.5" />
            </button>
          </div>
        </div>
        <span class="text-xs text-n-slate-10">
          {{ totalValue }}
        </span>
      </div>
    </div>
    <draggable
      v-model="localCards"
      item-key="id"
      group="kanban"
      class="flex flex-col gap-2 p-2 pb-20 md:pb-2 min-h-[120px] flex-1 overflow-y-auto"
      ghost-class="opacity-40"
      filter="button"
      :prevent-on-filter="false"
      @change="onCardMoved"
    >
      <template #item="{ element }">
        <KanbanCard
          :card="element"
          :selection-mode="props.selectionMode"
          :selected="props.selectedIds.includes(element.id)"
          @open-conversation="emit('openConversation', $event)"
          @card-click="emit('cardClick', $event)"
          @open-tasks="emit('openTasks', $event)"
          @select="emit('cardSelected', $event)"
        />
      </template>
    </draggable>
  </div>
</template>
